/**
 * STORY-STRIPE-006 — HQ Commission Payout Page
 *
 * Lists all commission_ledger rows with filter by status (pending/paid/all).
 * Lets HQ bulk-mark selected pending rows as paid via the new
 * POST /admin/commissions/mark-paid endpoint. Also supports single-row
 * payout via the existing PATCH /admin/commissions/:id/pay.
 *
 * Workflow:
 * 1. HQ sees pending total at the top (DollarSign stat card)
 * 2. Filter by agent or period month
 * 3. Select rows via checkbox
 * 4. Click "Mark N paid" → confirmation → bulk POST
 * 5. List refreshes, paid total goes up
 */
import { StatCard } from '@/components/custom/StatCard';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useApi } from '@/hooks/useApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Clock, DollarSign, Loader2, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

interface Commission {
  id: string;
  type: 'equipment_sale' | 'affiliate_sub' | 'individual_sub';
  amount_cents: number;
  gross_amount_cents: number;
  description: string | null;
  source_id: string;
  period_month: string;
  status: 'pending' | 'paid';
  created_at: string;
  agent: {
    id: string;
    name: string | null;
    territory: string | null;
  };
}

interface CommissionResponse {
  summary: {
    pending_total: number;
    paid_total: number;
    pending_count: number;
    paid_count: number;
  };
  commissions: Commission[];
  pagination: {
    has_more: boolean;
    next_cursor: string | null;
  };
}

function formatCents(cents: number) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(cents / 100);
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function typeLabel(type: Commission['type']) {
  switch (type) {
    case 'equipment_sale':
      return 'Equipment';
    case 'affiliate_sub':
      return 'Gym sub';
    case 'individual_sub':
      return 'Individual sub';
    default:
      return type;
  }
}

export default function AdminCommissions() {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'paid'>('pending');
  const [agentFilter, setAgentFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-commissions', statusFilter],
    queryFn: async () => {
      const response = await api.get<CommissionResponse>(
        `/admin/commissions?status=${statusFilter}&limit=200`
      );
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: async (ledgerIds: string[]) => {
      const response = await api.post<{
        requested: number;
        marked_paid: number;
        already_paid_or_missing: number;
      }>('/admin/commissions/mark-paid', { ledger_ids: ledgerIds });
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (result) => {
      toast.success(
        `${result?.marked_paid ?? 0} commission${(result?.marked_paid ?? 0) === 1 ? '' : 's'} marked paid`
      );
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['admin-commissions'] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to mark paid');
    },
  });

  // STORY-PAYOUT-1 — pay ALL of one agent's pending commission via a real
  // Stripe Connect transfer (vs. mark-paid which is a status flip only).
  const payConnectMutation = useMutation({
    mutationFn: async (agentId: string) => {
      const response = await api.post<{
        transfer_id: string;
        amount_cents: number;
        marked_paid: number;
      }>('/admin/commissions/pay-connect', { agent_id: agentId });
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (result) => {
      toast.success(
        `Paid ${formatCents(result?.amount_cents ?? 0)} via Stripe (${result?.marked_paid ?? 0} item${(result?.marked_paid ?? 0) === 1 ? '' : 's'})`
      );
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['admin-commissions'] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Stripe payout failed');
    },
  });

  const filteredCommissions = useMemo(() => {
    const rows = data?.commissions ?? [];
    if (!agentFilter.trim()) return rows;
    const needle = agentFilter.toLowerCase();
    return rows.filter(
      (c) =>
        c.agent.name?.toLowerCase().includes(needle) ||
        c.agent.territory?.toLowerCase().includes(needle)
    );
  }, [data?.commissions, agentFilter]);

  const pendingVisible = useMemo(
    () => filteredCommissions.filter((c) => c.status === 'pending'),
    [filteredCommissions]
  );

  const allPendingSelected =
    pendingVisible.length > 0 && pendingVisible.every((c) => selectedIds.has(c.id));

  const toggleAll = () => {
    if (allPendingSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingVisible.map((c) => c.id)));
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkMarkPaid = () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `Mark ${selectedIds.size} commission${selectedIds.size === 1 ? '' : 's'} as paid? This cannot be undone.`
      )
    ) {
      return;
    }
    markPaidMutation.mutate(Array.from(selectedIds));
  };

  const selectedTotal = useMemo(() => {
    return filteredCommissions
      .filter((c) => selectedIds.has(c.id))
      .reduce((sum, c) => sum + c.amount_cents, 0);
  }, [filteredCommissions, selectedIds]);

  // Stripe Connect pays an agent in full, so it's only offered when every
  // selected pending row belongs to a single agent.
  const selectedAgent = useMemo(() => {
    const selectedRows = filteredCommissions.filter(
      (c) => selectedIds.has(c.id) && c.status === 'pending'
    );
    if (selectedRows.length === 0) return null;
    const firstRow = selectedRows[0];
    if (!firstRow) return null;
    const agentId = firstRow.agent.id;
    return selectedRows.every((c) => c.agent.id === agentId)
      ? { id: agentId, name: firstRow.agent.name }
      : null;
  }, [filteredCommissions, selectedIds]);

  const handlePayConnect = () => {
    if (!selectedAgent) return;
    if (
      !window.confirm(
        `Pay ALL pending commission for ${selectedAgent.name ?? 'this agent'} via Stripe Connect? This sends real money to their connected bank account and cannot be undone.`
      )
    ) {
      return;
    }
    payConnectMutation.mutate(selectedAgent.id);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Agent Commissions</h1>
          <p className="mt-1 text-muted-foreground">
            Review pending agent payouts and mark them paid once settled.
          </p>
        </div>

        {/* Summary stats */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Pending payouts"
            value={formatCents(data?.summary.pending_total ?? 0)}
            icon={Clock}
          />
          <StatCard
            title="Paid to date"
            value={formatCents(data?.summary.paid_total ?? 0)}
            icon={CheckCircle}
          />
          <StatCard
            title="Pending rows"
            value={String(data?.summary.pending_count ?? 0)}
            icon={DollarSign}
          />
          <StatCard title="Paid rows" value={String(data?.summary.paid_count ?? 0)} icon={Users} />
        </div>

        {/* Filters + bulk action */}
        <Card>
          <CardHeader>
            <CardTitle>Commission ledger</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex gap-1 rounded-md border border-border p-1">
                {(['pending', 'paid', 'all'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    className={`rounded px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
                      statusFilter === s
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Input
                type="text"
                placeholder="Filter by agent name or territory"
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                className="max-w-xs"
              />
              <div className="ml-auto flex items-center gap-3">
                {selectedIds.size > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {selectedIds.size} selected — {formatCents(selectedTotal)}
                  </span>
                )}
                {selectedAgent && (
                  <Button
                    variant="default"
                    onClick={handlePayConnect}
                    disabled={payConnectMutation.isPending}
                    title="Send a real Stripe Connect transfer for all of this agent's pending commission"
                  >
                    {payConnectMutation.isPending && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    Pay via Stripe
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleBulkMarkPaid}
                  disabled={selectedIds.size === 0 || markPaidMutation.isPending}
                >
                  {markPaidMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Mark {selectedIds.size || ''} paid
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : error ? (
              <p className="py-6 text-sm text-destructive">
                Failed to load commissions: {(error as Error).message}
              </p>
            ) : filteredCommissions.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No commissions match the current filter.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="w-8 p-3">
                        <input
                          type="checkbox"
                          checked={allPendingSelected}
                          onChange={toggleAll}
                          disabled={pendingVisible.length === 0}
                          className="h-4 w-4"
                        />
                      </th>
                      <th className="p-3">Agent</th>
                      <th className="p-3">Type</th>
                      <th className="p-3">Description</th>
                      <th className="p-3">Period</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCommissions.map((c) => {
                      const isPending = c.status === 'pending';
                      return (
                        <tr key={c.id} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(c.id)}
                              onChange={() => toggleOne(c.id)}
                              disabled={!isPending}
                              className="h-4 w-4"
                            />
                          </td>
                          <td className="p-3">
                            <div className="font-medium">{c.agent.name ?? 'Unknown'}</div>
                            <div className="text-xs text-muted-foreground">
                              {c.agent.territory ?? '—'}
                            </div>
                          </td>
                          <td className="p-3 text-muted-foreground">{typeLabel(c.type)}</td>
                          <td className="p-3 text-muted-foreground max-w-xs truncate">
                            {c.description ?? c.source_id}
                          </td>
                          <td className="p-3 text-muted-foreground">{c.period_month}</td>
                          <td className="p-3 text-right font-medium">
                            {formatCents(c.amount_cents)}
                            <div className="text-xs text-muted-foreground font-normal">
                              gross {formatCents(c.gross_amount_cents)}
                            </div>
                          </td>
                          <td className="p-3">
                            {isPending ? (
                              <Badge
                                variant="outline"
                                className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20"
                              >
                                Pending
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-green-500/10 text-green-500 border-green-500/20"
                              >
                                Paid {formatDate(c.created_at)}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
